import { Feature } from '@turf/helpers';

export const fitBoundsOption = { duration: 0, padding: { top: 0, left: 0, bottom: 0, right: 500 } };

export type states = 'OVERVIEW' | 'OVERVIEW_V2' | 'DETAILS' | 'FIRST_SETUP' | 'SECOND_SETUP';

export const SF_PRACTICES_MAP = {
  seeding: 'RPBS',
  fertilization: 'RPF',
  grazing: 'RPIG',
  noTillage: 'NT',
  nitrogenOptimization: 'NO', // legacy support no longer use this status
  nitrogenReduction: 'NRR',
  coverCropping: 'CC',
  reducedTillage: 'IT',
  legume: 'LA',
  ICL1: 'ICL1',
  ICL2: 'ICL2',
  coverPerennial: 'CP',
  coverAnnual: 'CA',
  inoculant: 'IN',
};

export interface SyncErrors {
  practices: Array<{ name: string; sf: number; platform: number; option: string }>;
  newPractices: Array<{ name: string; sf: number; platform: number; option: string }>;
  paymentOptionErrors: Array<{ id: string; name: string; area: number }>;
  missingPractices: string[];
  status: string;
}

export const treatFieldPaddockData = (field): Feature[] => {
  return [
    ...field.layout.paddocks
      .filter((paddock: any) => !!paddock.boundary && Object.keys(paddock.boundary).length)
      .map((paddock: any) => {
        return {
          ...paddock.boundary,
          id: paddock.id,
          fieldId: field.id,
          layoutId: field.layout.id,
          properties: {
            ...(paddock.boundary?.properties ?? {}),
            name: paddock.name,
            area: paddock.area,
            areaHectare: paddock.areaHectare,
            status: paddock.status,
            fieldId: field.id,
            fieldName: field.name,
          },
        };
      }),
  ] as Feature[];
};

export const treatFieldData = (field: any): Feature => {
  const boundariesProps = field.processedIneligibleBoundaries;
  return {
    ...(boundariesProps?.original?.boundary ? boundariesProps.original?.boundary : field.boundaries),
    id: field.id,
    properties: {
      ...field,
      id: undefined,
      boundaries: undefined,
      parent: field.id,
      updatedAt: field.updatedAt,
      updatedBy: field.updatedBy,
      name: field.name,
      area: field.area,
      areaHectare: field.areaHectare,
      ineligibleAreaAcres: field.boundaries.properties?.ineligibleAreaAcres,
      ineligibleAreaHectares: field.boundaries.properties?.ineligibleAreaHectares,
      ineligibleAreasFinalized: field.boundaries.properties?.ineligibleAreasFinalized,
      situation: field.situation,
      leaseStartDate: field.leaseStartDate,
      leaseEndDate: field.leaseEndDate,
      leaseAgreementType: field.leaseAgreementType,
      practiceType: field.practiceType,
      practices: field.fieldPractices.map((practice: any) => practice.metadata.practiceName),
      paymentOption: field.paymentOption,
      county: field.county,
      country: field.country,
      state: field.state,
      irrigated: field.irrigated,
      legume: field.fieldPractices.find((practice: any) => !!practice.metadata.legume)?.metadata.legume === 'legume', // TODO : remove legume from field level and instead base everything off metadata directly
      previousTillage:
        field.fieldPractices.find((practice: any) => !!practice.metadata.previousTillage)?.metadata.previousTillage ??
        field.previousTillage ??
        'intensive', //Set to default in case previousTillage is null
      fieldPractices: field.fieldPractices,
      unenrolledAt: field.unenrolledAt,
    },
  };
};

export const producerFinishedDataCollection = (status, layout, producerData) => {
  return (
    (status.shapefileStatus === 'APPROVED' && !layout && producerData?.boundariesSynced) ||
    ['In Support', 'Ongoing Support', 'GST QA', 'Contract QA'].indexOf(producerData?.caseStatus) >= 0 ||
    (producerData?.boundariesSynced && producerData?.caseStatus?.toLowerCase() === 'Data Entry'.toLowerCase())
  );
};

export const producerStatusAhead = (caseStatus: string, brFinalizeBoundaries: boolean) => {
  return (
    (brFinalizeBoundaries && ['In Support', 'Ongoing Support', 'GST QA', 'Contract QA'].indexOf(caseStatus) !== -1) ||
    (!brFinalizeBoundaries && ['In Support', 'Ongoing Support', 'GST QA', 'Contract QA', 'Data Entry'].indexOf(caseStatus) !== -1)
  );
};

export const canEditBoundaries = (producerData: any, fields: any[], layout: any, isChannelPartner: boolean, shapefileStatus: string) => {
  return (
    (producerData?.caseStatus === 'Document & Data Collection' ||
      (!producerData?.boundariesSynced && producerData?.caseStatus?.toLowerCase() === 'Data Entry'.toLowerCase()) ||
      (((fields?.length && !layout) || !fields?.length) && !producerFinishedDataCollection(shapefileStatus, layout, producerData))) &&
    !isChannelPartner
  );
};
